import { firebase } from '@firebase/app';
import '@firebase/auth';
import router from '@/router';
import API from '@/api';
import get from 'lodash/get';
import clone from 'lodash/clone';
import isEmpty from 'lodash/isEmpty';
import { navigationTiles, accountMenuTiles } from '@/constants/variables';
// import posthog from 'posthog-js';
import { LOGIN_METHODS } from '@/api/auth';

const state = {
  user: {},
  reauthRequired: false,
  firebaseUser: {},
  token: {},
  currentFirm: {},
  accountMenu: [],
  navigationItems: [],
  mobileNavigationItems: [],
  notifications: [],
  myConversations: [],
  clientConversation: {},
  loginLoading: false,
  defaultCompany: {
    user: {},
  },
  savedConversationPageMessage: '',
  savedConversationDialogMessage: '',
};

const getters = {
  accountMenu(state) {
    return state.accountMenu || [];
  },
  navigationItems(state) {
    return state.navigationItems;
  },
  mobileNavigationItems(state) {
    return state.mobileNavigationItems;
  },
  user(state) {
    return state.user;
  },
  notifications(state) {
    return state.notifications;
  },
  currentUserDefaultCompanyId(state) {
    return get(state.user, 'default_company_id', null);
  },
  isAdvisor(state, getters) {
    return getters.isAuthenticated && state.user.is_advisor;
  },
  defaultCompany(state) {
    return state.defaultCompany;
  },
  currentFirm(state) {
    return state.currentFirm;
  },
  isFirmCommunicationDisabled(state) {
    return state.currentFirm.communication_disabled;
  },
  isClientDashboardTaxYearFilterEnabled() {
    return get(
      state.currentFirm,
      'preferences.enable_client_dashboard_tax_year_filter',
      false,
    );
  },
  isFirmDocumentsDisabled() {
    return state.currentFirm.documents_disabled;
  },
  isEmailRefDisabled() {
    return get(
      state.currentFirm,
      'preferences.hide_reply_to_ref_from_emails',
      false,
    );
  },
  isClientsDocumentsDisabled() {
    return get(
      state.currentFirm,
      'preferences.hide_documents_view_from_clients',
      false,
    );
  },
  isAuthenticated(state) {
    return state.user !== null && state.user !== undefined;
  },
  isSuperAdvisor(state) {
    return (
      state.user.email.includes('@soraban.com') &&
      state.user.email.includes('hello+')
    );
  },
  myConversations(state) {
    return state.myConversations;
  },
  clientConversation(state) {
    return state.clientConversation;
  },
  savedConversationPageMessage(state) {
    return state.savedConversationPageMessage;
  },
  savedConversationDialogMessage(state) {
    return state.savedConversationDialogMessage;
  },
  loginLoading(state) {
    return state.loginLoading;
  },
  reauthRequired(state) {
    return state.reauthRequired;
  },
};

const mutations = {
  setReauthRequired(state, payload) {
    state.reauthRequired = payload;
  },
  setUser(state, payload) {
    state.user = payload;
  },
  setFirebaseUser(state, payload) {
    state.firebaseUser = payload;
  },
  setToken(state, payload) {
    state.token = payload;
  },
  setDefaultCompany(state, payload) {
    state.defaultCompany = payload;
  },
  setCurrentFirm(state, payload) {
    state.currentFirm = payload;
  },
  setAccountMenu(state, payload) {
    state.accountMenu = payload || [];
  },
  setNavigationItems(state, payload) {
    state.navigationItems = payload;
  },
  setMobileNavigationItems(state, payload) {
    state.mobileNavigationItems = payload;
  },
  copyMobileFromNavigationItems(state, payload) {
    state.mobileNavigationItems = clone(state.navigationItems);
  },
  setAdvisorNotifications(state, payload) {
    state.notifications = clone(payload);
  },
  setMyConversations(state, payload) {
    state.myConversations = clone(payload);
  },
  updateConversationAsSeen(state, payload) {
    state.myConversations = state.myConversations.map(conversation => {
      if (conversation.id === payload) {
        return {
          ...conversation,
          unseen_messages: conversation.unseen_messages.map(message => {
            return {
              ...message,
              unseen_user_ids: message.unseen_user_ids.filter(id => {
                return id !== state.user.id;
              }),
            };
          }),
        };
      } else {
        return conversation;
      }
    });
  },
  setSavedConversationPageMessage(state, payload) {
    state.savedConversationPageMessage = clone(payload);
  },
  setSavedConversationDialogMessage(state, payload) {
    state.savedConversationDialogMessage = clone(payload);
  },

  setClientConversation(state, payload) {
    state.clientConversation = clone(payload);
  },
  setLoginLoading(state, payload) {
    state.loginLoading = clone(payload);
  },
};

const actions = {
  setNavigationItems({ commit }, items) {
    commit('setNavigationItems', items);
  },
  setAccountMenu({ commit }, accountMenu) {
    commit('setAccountMenu', accountMenu);
  },
  setMobileNavigationItems({ commit }, mobileNavigationItems) {
    commit('setMobileNavigationItems', mobileNavigationItems);
  },
  copyMobileFromNavigationItems({ commit }) {
    commit('copyMobileFromNavigationItems');
  },
  setAdvisorNotifications({ commit }, notifications) {
    commit('setAdvisorNotifications', notifications);
  },
  setMyConversations({ commit }, myConversations) {
    commit('setMyConversations', myConversations);
  },
  updateConversationAsSeen({ commit }, myConversations) {
    commit('updateConversationAsSeen', myConversations);
  },
  setSavedConversationPageMessage({ commit }, message) {
    commit('setSavedConversationPageMessage', message);
  },
  setSavedConversationDialogMessage({ commit }, message) {
    commit('setSavedConversationDialogMessage', message);
  },
  setClientConversation({ commit }, clientConversation) {
    commit('setClientConversation', clientConversation);
  },
  setLoginLoading({ commit }, loginLoading) {
    commit('setLoginLoading', loginLoading);
  },
  async getMyConversations({ commit, state }) {
    try {
      const response = await API.conversation.getMyConversations();
      if (!isEmpty(response.data)) {
        let conversations = response.data;
        if (state.user.is_advisor) {
          conversations = conversations
            .filter(a => a.last_message_datetime)
            .sort((a, b) =>
              a.last_message_datetime > b.last_message_datetime ? -1 : 1,
            );
        } else {
          conversations = conversations.sort((a, b) =>
            b.last_message_datetime > a.last_message_datetime ? 1 : -1,
          );
        }

        commit('setMyConversations', conversations);
      }
    } catch (e) {
      console.log(e);
    }
  },
  async setDefaultCompany({ commit, dispatch }, { companyId }) {
    try {
      const { data: defaultCompany } = await API.auth.setDefaultCompany(
        companyId,
      );
      commit('setDefaultCompany', defaultCompany);
    } catch (e) {
      console.log(e);
    } finally {
      dispatch('getCurrentUser');
    }
  },
  updateCurrentFirm({ commit }, firm) {
    commit('setCurrentFirm', firm);
  },
  async setCurrentFirm({ commit }) {
    try {
      const { data: currentFirm } = await API.firm.getCurrentUserFirm();
      commit('setCurrentFirm', currentFirm);
      return currentFirm;
    } catch (e) {
      console.log(e);
    }
  },
  resetTiles({ state, dispatch, getters }, payload) {
    if (state.user.is_advisor) {
      let userAccountMenuTiles = [
        accountMenuTiles.overviewMenuTile,
        accountMenuTiles.profileMenuTile,
      ];

      if (
        state.user.firm_access === 'full' ||
        state.user.firm_access === 'partner'
      ) {
        if (getters.isSuperAdvisor) {
          userAccountMenuTiles.push(accountMenuTiles.suggestedTasksMenuTile);
        }
      }
      if (
        state.user.firm_access === 'partner' &&
        state.currentFirm.my_cloud_enabled
      ) {
        userAccountMenuTiles.push(accountMenuTiles.myCloudMenuTile);
      }
      if (state.user.firm_access === 'partner') {
        userAccountMenuTiles.push(accountMenuTiles.manageFirmMenuTile);
        if (state.currentFirm.preferences['notification_summary_enabled']) {
          userAccountMenuTiles.push(
            accountMenuTiles.notificationsSummaryMenuTile,
          );
        }
      }
      dispatch('setAccountMenu', userAccountMenuTiles);

      if (payload) {
        const items = [navigationTiles.adminDashboardTile(payload)];
        if (!getters.isFirmDocumentsDisabled) {
          items.push(navigationTiles.documentsTile(payload));
        }
        if (!getters.isFirmCommunicationDisabled) {
          items.push(navigationTiles.conversationTile(payload));
        }
        if (state.currentFirm.review_and_export_enabled) {
          items.push(navigationTiles.reviewAndExportTile(payload));
        }
        dispatch('setNavigationItems', items);
      }
    } else {
      if (payload) {
        const dashboardMenuTile = accountMenuTiles.dashboardMenuTile(payload);

        dispatch('setAccountMenu', [dashboardMenuTile]);
      }
    }
    dispatch('copyMobileFromNavigationItems');
  },
  async confirm2FA(
    { commit, dispatch },
    { user, redirectPath, email, password },
  ) {
    await firebase.auth().signOut();
    commit('setUser', {});
    commit('setFirebaseUser', {});
    commit('setToken', {});
    commit('setLoginLoading', false);
    await firebase.auth().signInWithEmailAndPassword(email, password);
    await dispatch('getCurrentUser').then(async currentUser => {
      // posthog.capture('signed in', {
      //   user_id: currentUser.id,
      // });
      await dispatch('setCurrentFirm');
      await dispatch('companies/getCompanies');
      await dispatch('setDefaultCompany', {
        companyId: currentUser.default_company_id,
      });

      if (redirectPath) {
        router.replace(redirectPath);
      } else {
        router.replace('/overview');
      }
    });
  },
  async userLogin(
    { commit, dispatch },
    { username, password, authToken, redirectPath, isAuthenticated },
  ) {
    const signInPromise = authToken
      ? firebase.auth().signInWithCustomToken(authToken)
      : firebase.auth().signInWithEmailAndPassword(username, password);

    let response = null;

    await signInPromise
      .then(async _user => {
        await dispatch('getCurrentUser').then(async currentUser => {
          if (currentUser) {
            commit('setReauthRequired', false);

            // Track login: api
            API.auth.logSignIn({
              method: authToken
                ? LOGIN_METHODS.AUTH_TOKEN
                : LOGIN_METHODS.EMAIL,
            });

            // Track login: posthog
            // posthog.capture('signed in', {
            //   user_id: currentUser.id,
            // });

            // Flow: continue 2fa flow
            if (currentUser.enabled_2fa && !isAuthenticated) {
              response = 'REQUEST_2FA';
            }

            // Flow: Regular login
            else {
              await dispatch('setCurrentFirm');
              await dispatch('setDefaultCompany', {
                companyId: currentUser.default_company_id,
              });

              let companies = [];

              if (!currentUser.is_advisor) {
                companies = await dispatch('companies/getCompanies');
              }

              const hasInvalidCompanyId =
                typeof router.currentRoute.params['companyId'] === 'undefined';

              // Password required
              if (currentUser.set_password_required) {
                const passwordRedirectQuery = redirectPath
                  ? `?redirect=${encodeURIComponent(redirectPath)}`
                  : '';
                const passwordRequiredPath = currentUser.is_advisor
                  ? '/set-password'
                  : '/set-sign-in-method';

                if (currentUser.is_advisor) {
                  await dispatch('companies/getCompanies');
                }

                return router.replace(
                  passwordRequiredPath + passwordRedirectQuery,
                );
              }

              // As an advisor
              else if (currentUser.is_advisor) {
                if (redirectPath) {
                  await dispatch('companies/getCompanies');
                  return router.replace(redirectPath);
                } else {
                  return router.replace('/overview');
                }
              }

              // With invalid company id
              else if (
                !redirectPath &&
                hasInvalidCompanyId &&
                companies.length >= 2
              ) {
                return router.replace('/select-entity');
              }

              // Fallback
              else {
                if (redirectPath) {
                  router.replace(redirectPath);
                } else {
                  router.replace('/');
                }
              }
            }
          }

          commit('setLoginLoading', false);
        });
      })
      .catch(e => {
        if (e.code === 'auth/invalid-custom-token') {
          commit('setReauthRequired', true);
          commit('setUser', {});
          commit('setLoginLoading', false);
        } else if (e.code === 'auth/too-many-requests') {
          dispatch('application/setErrorNotification', {
            message:
              'Too many failed attempts. Please try again in a few minutes.',
          });
          commit('setUser', {});
          commit('setLoginLoading', false);
        } else if (e.code === 'auth/wrong-password') {
          dispatch('application/setErrorNotification', {
            message: 'Incorrect password. Please try again.',
          });
          commit('setUser', {});
          commit('setLoginLoading', false);
        } else {
          dispatch('application/setErrorNotification', {
            message:
              'Sorry. An error occured. Our team has been notified about this error.',
            errorToSend: e,
            user_email: username,
          });
          commit('setUser', {});
          commit('setLoginLoading', false);
        }
      });

    return response;
  },
  userRelogin({ commit, dispatch }, { email, password, redirectPath }) {
    firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(user => {
        dispatch('getCurrentUser').then(async currentUser => {
          if (currentUser) {
            await dispatch('companies/getCompanies');
            await dispatch('setDefaultCompany', {
              companyId: currentUser.default_company_id,
            });
          }
        });
      })
      .catch(e => {
        dispatch('application/setErrorNotification', {
          message: 'Incorrect password. Please try again.',
        });
        commit('setUser', {});
        commit('setLoginLoading', false);
        router.push('/signin');
      });
  },
  userJoin({ commit, dispatch }, { email, password }) {
    firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(user => {
        dispatch('getCurrentUser').then(async currentUser => {
          await dispatch('companies/getCompanies');
          await dispatch('setDefaultCompany', {
            companyId: currentUser.default_company_id,
          });
        });
        // TODO: pass user.user.uid to backend and associate with this email and create that in the database.
        // user.user.uid
        // commit('setUser', user);
        router.push('/');
      })
      .catch(() => {
        commit('setUser', {});
      });
  },
  userSignOut({ commit }) {
    firebase
      .auth()
      .signOut()
      .then(() => {
        if (window.HubSpotConversations && window.HubSpotConversations.widget) {
          window.HubSpotConversations.widget.remove();
        }

        commit('setUser', {});
        commit('setFirebaseUser', {});
        commit('setToken', {});
        if (get(router, 'currentRoute.meta.requiresAuth', false)) {
          router.push('/signin');
        }

        commit('setLoginLoading', false);
      })
      .catch(() => {
        commit('setUser', {});
        commit('setFirebaseUser', {});
        commit('setToken', {});
        router.push('/signin');
        commit('setLoginLoading', false);
      });
  },
  async userSignOutSilently({ commit }) {
    try {
      await firebase.auth().signOut();
      if (window.HubSpotConversations && window.HubSpotConversations.widget) {
        window.HubSpotConversations.widget.remove();
      }
    } finally {
      commit('setUser', {});
      commit('setFirebaseUser', {});
      commit('setToken', {});
      commit('setLoginLoading', false);
    }
  },
  async getFirebaseUser({ commit, state }) {
    if (!isEmpty(state.firebaseUser)) {
      return state.firebaseUser;
    }
    const user = await firebase.auth().currentUser;
    commit('setFirebaseUser', user);
    return user;
  },
  async getToken({ commit, state }) {
    try {
      const token = await state.firebaseUser.getIdToken();
      commit('setToken', token);
      return token;
    } catch (error) {
      console.log(error);
    }
  },
  async getCurrentUser({ dispatch, commit }) {
    try {
      const response = await API.auth.getCurrentUser();
      if (response.data.error) {
        console.log(response.data.error);
        dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
        commit('setUser', {});
      } else {
        let user = response.data;

        // Attach an empty overview_setting if not present
        if (user && !user.overview_setting) {
          user['overview_setting'] = {};
        }

        commit('setUser', user);
        return user;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log(error.message);
      } else {
        dispatch('application/setErrorNotification', {
          message: error.message || 'Error loading user information',
        });
      }
      dispatch('userSignOut');
      commit('setLoginLoading', false);
      return {};
    }
  },
  async verifyToken({ commit }, idToken) {
    await API.auth.verifyToken(idToken);
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
  // namespaced: true,
};
